import { type PropsWithChildren, useCallback, useState } from 'react'

import { AuthContext } from './AuthContext'
import { useLocalStorage } from '@/hooks'
import { logger } from '@/utils'
import { useNavigate, type ParsedLocation } from '@tanstack/react-router'

const log = logger.child({
  file: 'src/providers/AuthProvider/AuthProvider.tsx',
})
export type Tokens = {
  access_token: string
  refresh_token: string
}

export type AuthContextType = Partial<Tokens> & {
  login: (tokens: Tokens) => Promise<void>
  refreshAccessToken: (accessToken: string) => Promise<void>
  logout: (options?: {
    apiLogoutFunction?: () => Promise<void>
    errorMsg?: string
    redirect?: string | ParsedLocation
    loggedOutMessage?: string
  }) => Promise<void>
  setRedirectUrl: (location: string | ParsedLocation) => void
  setErrorMsg: (errMsg?: string) => void
  redirectUrl: string | ParsedLocation
  errorMsg?: string
  authenticated?: boolean
  loggedOutMessage?: string | undefined
  setLoggedOutMessage: (msg?: string) => void
}

export type AuthProviderProps = PropsWithChildren<{
  defaultRedirect: string | ParsedLocation
  authCheck?: (payload: object) => Promise<Response | Error>
}>

export const AuthProvider = ({
  children,
  defaultRedirect = '/',
  authCheck,
}: AuthProviderProps) => {
  const navigate = useNavigate()
  // stores the jwt in localstorage
  const [access_token, setAccessToken] = useLocalStorage<
    AuthContextType['access_token'] | undefined
  >('access_token')
  const [refresh_token, setRefreshToken] = useLocalStorage<
    AuthContextType['refresh_token'] | undefined
  >('refresh_token')

  const [redirectUrl, setRedirectUrlState] =
    useState<AuthContextType['redirectUrl']>(defaultRedirect)

  const [errorMsg, setErrorMsg] = useState<AuthContextType['errorMsg']>()

  const [loggedOutMessage, setLoggedOutMessage] =
    useState<AuthContextType['loggedOutMessage']>()

  const setRedirectUrl: AuthContextType['setRedirectUrl'] = useCallback(
    (url) => {
      if (!url) {
        return
      }
      setRedirectUrlState(url)
    },
    [],
  )
  const login: AuthContextType['login'] = useCallback(
    async (tokens) => {
      try {
        setLoggedOutMessage(undefined)
        // optional auth check like making a request with the new jwt
        await authCheck?.({})
        // store tokens
        setAccessToken(tokens.access_token)
        setRefreshToken(tokens.refresh_token)

        // Handle navigation based on redirectUrl type
        if (typeof redirectUrl === 'string') {
          await navigate({ to: redirectUrl })
        } else {
          const { pathname, search, hash } = redirectUrl
          await navigate({ to: pathname, search, hash })
        }

        setRedirectUrlState(defaultRedirect)
      } catch (e) {
        log.debug(e)
        setErrorMsg('error logging in')
      }
    },
    [
      authCheck,
      defaultRedirect,
      navigate,
      redirectUrl,
      setAccessToken,
      setRefreshToken,
    ],
  )
  const refreshAccessToken: AuthContextType['refreshAccessToken'] = useCallback(
    async (access_token) => {
      try {
        setLoggedOutMessage(undefined)
        // optional auth check like making a request with the new jwt
        await authCheck?.({})
        // store tokens
        setAccessToken(access_token)
      } catch (e) {
        log.debug(e)
        setErrorMsg('error logging in')
      }
    },
    [authCheck, setAccessToken],
  )

  const logout: AuthContextType['logout'] = useCallback(
    async (options) => {
      console.error('logout', options)
      const {
        redirect = defaultRedirect,
        errorMsg,
        loggedOutMessage,
        apiLogoutFunction,
      } = options || {}
      /*
      reset the access tokens this will trigger the re-direct to login
      elsewhere in the app
      */
      await apiLogoutFunction?.().catch(() => {
        // remove tokens on fail
        setAccessToken(undefined)
        setRefreshToken(undefined)
      })
      setAccessToken(undefined)
      setRefreshToken(undefined)
      setRedirectUrl(redirect)
      setErrorMsg(errorMsg)
      setLoggedOutMessage(loggedOutMessage)
    },
    [defaultRedirect, setAccessToken, setRedirectUrl, setRefreshToken],
  )

  return (
    <AuthContext.Provider
      value={{
        access_token,
        refresh_token,
        login,
        refreshAccessToken,
        logout,
        setRedirectUrl,
        setErrorMsg,
        redirectUrl,
        errorMsg,
        authenticated: !!access_token,
        loggedOutMessage,
        setLoggedOutMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
